import { useState, useEffect, useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { Eye, CircleNotch, FunnelSimple, X, DownloadSimple, WarningCircle, Sparkle, CaretUp, CaretDown, CaretUpDown, Paperclip } from "@phosphor-icons/react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem } from "@/components/ui/command";
import { NavTabs } from "@/components/NavTabs";
import { SapConnectionStatus } from "@/components/SapConnectionStatus";
import { ErpConnectionStatus } from "@/components/ErpConnectionStatus";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EMPTY_FILTERS = { supplier_code: "", site_id: "", product_id: "", po_number: "", created_by: "", po_date_from: "", po_date_to: "", po_type: "", order_status: "" };

// Oct 2026, user's explicit ask (Created POs Default 7-Day Filter + KPI
// dashboard) - IST calendar-day math done purely via UTC epoch-ms offset
// (same 5h30m IST_OFFSET convention the backend uses, fg_consumption_
// service.IST_OFFSET) so "today"/"last 7 days" means the same calendar
// day on screen regardless of the browser's own local timezone.
const IST_OFFSET_MS = (5 * 60 + 30) * 60 * 1000;
const istDateString = (epochMs) => new Date(epochMs + IST_OFFSET_MS).toISOString().slice(0, 10);
const istDateStringDaysAgo = (daysAgo) => istDateString(Date.now() - daysAgo * 86400000);
const DATE_PRESETS = {
  today: () => ({ po_date_from: istDateStringDaysAgo(0), po_date_to: istDateStringDaysAgo(0) }),
  last7: () => ({ po_date_from: istDateStringDaysAgo(6), po_date_to: istDateStringDaysAgo(0) }),
  last30: () => ({ po_date_from: istDateStringDaysAgo(29), po_date_to: istDateStringDaysAgo(0) }),
  thismonth: () => ({ po_date_from: `${istDateStringDaysAgo(0).slice(0, 7)}-01`, po_date_to: istDateStringDaysAgo(0) }),
};
const DATE_PRESET_OPTIONS = [
  { key: "today", label: "Today" },
  { key: "last7", label: "Last 7 Days" },
  { key: "last30", label: "Last 30 Days" },
  { key: "thismonth", label: "This Month" },
  { key: "custom", label: "Custom Range" },
];
const formatPoValue = (value, currency) => {
  if (currency === "INR") {
    if (Math.abs(value) < 100000) return `\u20b9${value.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
    return `\u20b9${(value / 100000).toFixed(2)}L`;
  }
  const symbol = currency === "USD" ? "$" : currency === "EUR" ? "\u20ac" : currency === "GBP" ? "\u00a3" : `${currency} `;
  return `${symbol}${value.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
};

const PO_TYPE_LABELS = { material: "Material", service: "Service & Consume", jobwork: "Job Work", capital: "Capital", interplant: "Interplant" };
const PO_TYPE_BADGE_CLASSES = {
  material: "bg-[#EFF8FF] text-[#175CD3] border-[#B2DDFF]",
  service: "bg-[#ECFDF3] text-[#027A48] border-[#ABEFC6]",
  jobwork: "bg-[#FFF6ED] text-[#B93815] border-[#FDDCAB]",
  capital: "bg-[#F4F3FF] text-[#5925DC] border-[#D9D6FE]",
  interplant: "bg-[#FDF2FA] text-[#C11574] border-[#FCCEEE]",
};
const PoTypeBadge = ({ poType }) => (
  <span className={`inline-block px-1.5 py-0.5 rounded-sm text-[10px] font-semibold border font-heading uppercase ${PO_TYPE_BADGE_CLASSES[poType] || PO_TYPE_BADGE_CLASSES.material}`}>
    {PO_TYPE_LABELS[poType] || poType || "Material"}
  </span>
);

// Feb 2026, user's explicit ask (real report: a PO created by one user
// later got cancelled by another via the app's own Cancel PO button, with
// no record visible here) - green "Created" vs red "Cancelled" chip, with
// who/when shown as a sub-line when cancelled (data joined server-side
// from po_cancel_audit_log, cache-only, same as every other column here).
const OrderStatusCell = ({ po }) => (
  po.cancelled_at ? (
    <div data-testid={`created-pos-status-cancelled-${po.po_number}`}>
      <span className="inline-block px-1.5 py-0.5 rounded-sm text-[10px] font-semibold border font-heading uppercase bg-[#FEF3F2] text-[#B42318] border-[#FDA29B]">
        Cancelled
      </span>
      <p className="text-[10px] text-[#667085] mt-0.5 whitespace-nowrap">
        by {po.cancelled_by || "—"}<br />{new Date(po.cancelled_at).toLocaleString()}
      </p>
    </div>
  ) : (
    <span className="inline-block px-1.5 py-0.5 rounded-sm text-[10px] font-semibold border font-heading uppercase bg-[#ECFDF3] text-[#027A48] border-[#ABEFC6]" data-testid={`created-pos-status-created-${po.po_number}`}>
      Created
    </span>
  )
);

// Column-sort config - client-side only (the full filtered page of up to
// 200 rows is already loaded in memory), click a header to sort asc, click
// again for desc, a third click clears back to the backend's default order
// (newest created_at first). `accessor` resolves the comparable value.
const COLUMNS = [
  { label: "SAP PO #", key: "po_number" },
  { label: "Printed PO #", key: "sap_po_number" },
  { label: "Type", key: "po_type" },
  { label: "Order Status", key: "cancelled_at" },
  { label: "Supplier", key: "supplier_code" },
  { label: "Site", key: "purchase_unit_site" },
  { label: "Bill-To", key: "bill_to_company" },
  { label: "PO Date", key: "po_date" },
  { label: "PR Number", key: "pr_number" },
  { label: "Items", key: "items_count", accessor: (po) => (po.items || []).length },
  { label: "Created By", key: "created_by" },
  { label: "Created At", key: "created_at" },
  { label: "", key: null },
];

const getSortValue = (po, col) => (col.accessor ? col.accessor(po) : po[col.key]);

export default function CreatedPurchaseOrdersPage() {
  const [pos, setPos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);
  const [filters, setFilters] = useState(() => {
    const hasPoDeepLink = new URLSearchParams(window.location.search).get("po");
    // Oct 2026 fix (testing-agent regression report) - a deep-linked PO
    // can be older than the new default 7-day window, so don't apply
    // that default when arriving via ?po=<number>; load full history
    // instead so the target row is still found.
    return hasPoDeepLink ? EMPTY_FILTERS : { ...EMPTY_FILTERS, ...DATE_PRESETS.last7() };
  });
  const [datePreset, setDatePreset] = useState(() => (new URLSearchParams(window.location.search).get("po") ? "custom" : "last7"));
  const [productFilterInput, setProductFilterInput] = useState("");
  const [poNumberFilterInput, setPoNumberFilterInput] = useState("");
  const [sites, setSites] = useState([]);
  const [filterOptions, setFilterOptions] = useState({ suppliers: [], created_by: [] });
  const [kpis, setKpis] = useState(null);
  const [kpisLoading, setKpisLoading] = useState(true);
  const [searchParams, setSearchParams] = useSearchParams();
  // Oct 1 2026, user's explicit ask: "fetch PO print pdf from SAP, only
  // when order status is released" + auto re-fetch on every click (no
  // stale cache) - loadingPo tracks which row's button is mid-fetch
  // (keyed "{po_number}:pdf" or "{po_number}:ai" since Feb 2026 added
  // a second, separate button - see fetchAndOpenPdf below).
  const [loadingPo, setLoadingPo] = useState(null);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: null });
  const [supplierPickerOpen, setSupplierPickerOpen] = useState(false);
  // Feb 2026, user's explicit ask - a PO still "In Preparation" in SAP
  // gets a RED Print button (see po.in_preparation, joined server-side
  // from a cached live SAP check); clicking it re-checks SAP right now
  // and pops up SAP's OWN real reason instead of a generic message.
  const [prepCheckDialog, setPrepCheckDialog] = useState(null); // {po_number, messages}

  const fetchAndOpenPdf = useCallback((poNumber, endpointPath, loadingKey) => {
    setLoadingPo(loadingKey);
    // 28s client-side timeout, slightly above the backend's own 25s bound
    // on the SAP call, so a slow SAP response always surfaces the
    // backend's clean `detail` message instead of the browser silently
    // hanging or a raw gateway error page with no usable detail.
    axios.get(`${API}/purchase-orders/${poNumber}/${endpointPath}`, { timeout: 28000 })
      .then((r) => {
        const data = r.data || {};
        if (!data.released) {
          toast.error(data.error || `PO ${poNumber} is not yet Released in SAP.`);
          return;
        }
        if (!data.available) {
          toast.error(data.error || `SAP hasn't generated a print form for PO ${poNumber} yet.`);
          return;
        }
        const byteChars = atob(data.pdf_base64);
        const bytes = new Uint8Array(byteChars.length);
        for (let i = 0; i < byteChars.length; i++) bytes[i] = byteChars.charCodeAt(i);
        const blob = new Blob([bytes], { type: "application/pdf" });
        window.open(URL.createObjectURL(blob), "_blank");
      })
      .catch((e) => {
        const detail = e?.response?.data?.detail;
        if (detail) {
          toast.error(detail);
        } else if (e?.code === "ECONNABORTED") {
          toast.error(`Timed out waiting for SAP to respond for PO ${poNumber}. Please try again.`);
        } else {
          toast.error(`Could not reach the server to fetch PO ${poNumber}'s PDF. Please check your connection and try again.`);
        }
      })
      .finally(() => setLoadingPo(null));
  }, []);

  // Feb 2026, user's explicit ask: NEW, separate "Print AI Order" -
  // in-app-generated PDF replica with the correct printed PO number
  // (the old "View Print PDF" button, SAP's own native output, was
  // removed per the user's later explicit ask - Oct 17 2026 - to only
  // allow downloading the Emergent-generated PDF from this page).
  const viewPrintAiPdf = useCallback((poNumber) => fetchAndOpenPdf(poNumber, "print-ai-pdf", `${poNumber}:ai`), [fetchAndOpenPdf]);

  // Feb 2026, user's explicit ask - clicking the RED Print button (PO
  // still "In Preparation" per the cached flag) checks SAP live right
  // now instead of just trusting the cache, and either pops up SAP's
  // real reason or - if staff already fixed it in SAP since the last
  // background refresh - proceeds straight to the normal PDF fetch.
  const checkPrepStatusThenMaybeOpenPdf = useCallback((poNumber) => {
    const loadingKey = `${poNumber}:ai`;
    setLoadingPo(loadingKey);
    axios.get(`${API}/purchase-orders/${poNumber}/prep-check`, { timeout: 28000 })
      .then((r) => {
        const data = r.data || {};
        setPos((prev) => prev.map((p) => (p.po_number === poNumber ? { ...p, in_preparation: !!data.in_preparation, sap_prep_messages: data.messages || [] } : p)));
        if (data.in_preparation) {
          setPrepCheckDialog({ po_number: poNumber, messages: data.messages || [] });
          setLoadingPo(null);
        } else {
          viewPrintAiPdf(poNumber);
        }
      })
      .catch(() => {
        toast.error(`Could not check SAP status for PO ${poNumber} right now. Please try again.`);
        setLoadingPo(null);
      });
  }, [viewPrintAiPdf]);

  const handlePdfButtonClick = useCallback((po) => {
    if (po.in_preparation) {
      checkPrepStatusThenMaybeOpenPdf(po.po_number);
    } else {
      viewPrintAiPdf(po.po_number);
    }
  }, [checkPrepStatusThenMaybeOpenPdf, viewPrintAiPdf]);

  // Feb 2026, user's explicit ask: "we apply mandatory attachment of
  // mail [for Interplant POs]... that attachment is not visible right
  // now any of the page - show that attachment [in the] PO view tab so
  // anyone can verify why this interplant PO was made" - the mandatory
  // proof-of-approval email (see server.py's INTERPLANT_ATTACHMENT_*/
  // upload_interplant_po_attachment) was already stored at creation
  // time but never surfaced on this list's View dialog. Mirrors
  // GrnApprovalPage.jsx's viewInvoiceAttachment pattern (blob fetch,
  // not a raw <a href> - this endpoint needs the session cookie and
  // returns binary content).
  const viewInterplantAttachment = useCallback((poNumber) => {
    const loadingKey = `${poNumber}:attachment`;
    setLoadingPo(loadingKey);
    axios.get(`${API}/interplant-purchase-orders/${poNumber}/attachment`, { responseType: "blob" })
      .then((resp) => {
        window.open(window.URL.createObjectURL(resp.data), "_blank");
      })
      .catch(() => {
        toast.error(`Could not open the attachment for PO ${poNumber}`);
      })
      .finally(() => setLoadingPo(null));
  }, []);

  useEffect(() => {
    axios.get(`${API}/purchase-orders/sites`).then((r) => setSites(r.data?.sites || [])).catch(() => {});
    axios.get(`${API}/purchase-orders/history/filter-options`).then((r) => setFilterOptions(r.data || { suppliers: [], created_by: [] })).catch(() => {});
  }, []);

  const loadHistory = useCallback(() => {
    setLoading(true);
    const controller = new AbortController();
    const params = { limit: 200 };
    Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
    axios.get(`${API}/purchase-orders/history`, { params, signal: controller.signal })
      .then((r) => setPos(r.data || []))
      .catch((e) => { if (axios.isCancel(e)) return; })
      .finally(() => { if (!controller.signal.aborted) setLoading(false); });
    return () => controller.abort();
  }, [filters]);

  useEffect(() => loadHistory(), [loadHistory]);

  // Oct 2026, user's explicit ask (compact KPI dashboard) - same filters
  // as the table above, but a separate lightweight call: the backend
  // aggregates Total/By Site/By User/Value-by-Site over the FULL
  // filtered dataset (never just the 200-row page loaded above).
  // AbortController guards against a rapid preset/filter click letting
  // an older, slower response overwrite a newer one (testing-agent
  // code-review note).
  const loadKpis = useCallback(() => {
    setKpisLoading(true);
    const controller = new AbortController();
    const params = {};
    Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
    axios.get(`${API}/purchase-orders/history/kpis`, { params, signal: controller.signal })
      .then((r) => setKpis(r.data || null))
      .catch((e) => { if (!axios.isCancel(e)) setKpis(null); })
      .finally(() => { if (!controller.signal.aborted) setKpisLoading(false); });
    return () => controller.abort();
  }, [filters]);

  useEffect(() => loadKpis(), [loadKpis]);

  // Deep-link support: ?po=<number> from the PO Creation success toast/dialog auto-opens that row's detail
  useEffect(() => {
    const targetPo = searchParams.get("po");
    if (!targetPo || loading || pos.length === 0) return;
    const match = pos.find((p) => p.po_number === targetPo);
    if (match) {
      setDetail(match);
      searchParams.delete("po");
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, pos, loading, setSearchParams]);

  // Oct 6 2026 fix (real user report - PO 30004's price in this modal
  // didn't match SAP's current, corrected value): used to OVERWRITE the
  // stored approved-price snapshot with SAP's live value - silently
  // masking an unauthorized SAP edit. Now read-only: fetches SAP's live
  // price as `live_unit_price` alongside the untouched approved
  // `unit_price`, so a mismatch can be flagged instead of hidden (see
  // the "Price Mismatch" badge below). Best-effort - keeps showing the
  // approved snapshot alone if SAP/the fetch call fails.
  useEffect(() => {
    if (!detail) return;
    const poNumber = detail.po_number;
    axios.post(`${API}/purchase-orders/${poNumber}/refresh-prices`)
      .then(({ data }) => {
        setDetail((prev) => (prev && prev.po_number === poNumber ? { ...prev, items: data.items, sap_po_number: data.sap_po_number || prev.sap_po_number } : prev));
        // Oct 14 2026, user's explicit ask - keep the table row's Printed
        // PO # column in sync too, not just the detail modal, the moment
        // this live self-heal resolves it.
        if (data.sap_po_number) {
          setPos((prev) => prev.map((p) => (p.po_number === poNumber ? { ...p, sap_po_number: data.sap_po_number } : p)));
        }
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detail?.po_number]);

  const setFilter = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    if (key === "po_date_from" || key === "po_date_to") setDatePreset("custom");
  };
  const applyDatePreset = (key) => {
    setDatePreset(key);
    if (key !== "custom") setFilters((prev) => ({ ...prev, ...DATE_PRESETS[key]() }));
  };
  const clearFilters = () => { setFilters(EMPTY_FILTERS); setDatePreset(null); setProductFilterInput(""); setPoNumberFilterInput(""); };
  const activeFilterCount = Object.values(filters).filter(Boolean).length;

  const toggleSort = (key) => {
    setSortConfig((prev) => {
      if (prev.key !== key) return { key, direction: "asc" };
      if (prev.direction === "asc") return { key, direction: "desc" };
      return { key: null, direction: null };
    });
  };

  const sortedPos = useMemo(() => {
    const { key, direction } = sortConfig;
    if (!key) return pos;
    const col = COLUMNS.find((c) => c.key === key);
    const sorted = [...pos].sort((a, b) => {
      const va = getSortValue(a, col);
      const vb = getSortValue(b, col);
      const aEmpty = va == null || va === "";
      const bEmpty = vb == null || vb === "";
      // Empty/missing values always sort last, in BOTH directions - a
      // direction multiplier on the comparison (not .reverse() on the
      // whole array) is what keeps that true for desc too.
      if (aEmpty && bEmpty) return 0;
      if (aEmpty) return 1;
      if (bEmpty) return -1;
      const cmp = typeof va === "number" && typeof vb === "number"
        ? va - vb
        : String(va).localeCompare(String(vb), undefined, { numeric: true, sensitivity: "base" });
      return direction === "desc" ? -cmp : cmp;
    });
    return sorted;
  }, [pos, sortConfig]);

  const selectedSupplier = filterOptions.suppliers.find((s) => s.code === filters.supplier_code);
  const supplierLabel = filters.supplier_code
    ? (selectedSupplier ? `${selectedSupplier.code} - ${selectedSupplier.name}` : filters.supplier_code)
    : "All Suppliers";

  const fmtDate = (v) => (v ? new Date(v).toLocaleString() : "—");

  const exportCsv = () => {
    const headers = ["SAP PO #", "Printed PO #", "Type", "Order Status", "Cancelled By", "Cancelled At", "Supplier", "Site", "Bill-To", "PO Date", "PR Number", "Items", "Created By", "Created At"];
    const csvEscape = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const rows = sortedPos.map((po) => [
      po.po_number, po.sap_po_number || "", PO_TYPE_LABELS[po.po_type] || po.po_type || "Material",
      po.cancelled_at ? "Cancelled" : "Created", po.cancelled_by || "", fmtDate(po.cancelled_at),
      po.supplier_code, po.purchase_unit_site, po.bill_to_company,
      po.po_date, po.pr_number || "", (po.items || []).length, po.created_by || "", fmtDate(po.created_at),
    ].map(csvEscape).join(","));
    const csv = [headers.map(csvEscape).join(","), ...rows].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `created-purchase-orders-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="min-h-screen bg-[#F5F6F7] flex flex-col font-sans">
      {/* Sep 7 2026 fix - real bug, "menu is not visible on this page":
          this header used a plain white bar while every other page uses a
          teal (#0E7C86) header - NavTabs/SapConnectionStatus render WHITE
          text (by design, meant to sit on that teal bar), so on a white
          background they were rendering but invisible (white-on-white).
          Only the active/current NavTabs item showed (its text turns teal
          when active). Matched the standard header used everywhere else. */}
      <header className="min-h-16 bg-[#0E7C86] shadow-[0_1px_3px_0_rgba(16,24,40,0.15)] flex items-center justify-between px-3 sm:px-5 shrink-0 z-10 gap-2 sm:gap-4">
        <div className="flex items-center gap-3 flex-1 justify-start min-w-0">
          <NavTabs />
        </div>
        <SapConnectionStatus />
        <ErpConnectionStatus />
      </header>

      <main className="flex-1 overflow-auto max-w-[1400px] w-full mx-auto px-6 py-6 space-y-4">
        <div className="flex items-center justify-between gap-3">
          <div>
            <h1 className="font-heading text-xl font-bold text-[#1D2939]" data-testid="created-pos-page-title">Created Purchase Orders</h1>
            <p className="text-sm text-[#667085] mt-0.5">Every Purchase Order pushed to SAP ByDesign from this app, with its live SAP reference number.</p>
          </div>
          <Button
            type="button"
            variant="outline"
            className="rounded-sm border-[#D0D5DD] text-[#344054] shrink-0"
            disabled={pos.length === 0}
            onClick={exportCsv}
            data-testid="created-pos-export-csv-button"
          >
            <DownloadSimple size={14} className="mr-1.5" /> Export CSV
          </Button>
        </div>

        <div className="bg-white border border-[#D0D5DD] rounded-sm p-3" data-testid="created-pos-filter-bar">
          <div className="flex items-center gap-2 mb-2">
            <FunnelSimple size={14} className="text-[#004B87]" />
            <span className="text-xs font-bold text-[#344054] font-heading uppercase">Filters</span>
            {activeFilterCount > 0 && (
              <Button type="button" variant="ghost" size="sm" className="h-6 px-2 text-[11px] text-[#B42318] hover:bg-[#FEF3F2]" onClick={clearFilters} data-testid="created-pos-clear-filters-button">
                <X size={11} className="mr-1" /> Clear ({activeFilterCount})
              </Button>
            )}
          </div>
          <div className="flex items-center gap-1.5 mb-2 flex-wrap" data-testid="created-pos-date-presets">
            {DATE_PRESET_OPTIONS.map((p) => (
              <button
                key={p.key}
                type="button"
                onClick={() => applyDatePreset(p.key)}
                className={`px-2.5 py-1 rounded-full text-[11px] font-semibold border transition-colors ${
                  datePreset === p.key ? "bg-[#004B87] text-white border-[#004B87]" : "bg-white text-[#475467] border-[#D0D5DD] hover:border-[#98A2B3]"
                }`}
                data-testid={`created-pos-date-preset-${p.key}`}
              >
                {p.label}
              </button>
            ))}
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-9 gap-2">
            <Select value={filters.po_type || "__all__"} onValueChange={(v) => setFilter("po_type", v === "__all__" ? "" : v)}>
              <SelectTrigger className="h-8 text-xs rounded-sm" data-testid="created-pos-filter-type"><SelectValue placeholder="Type" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">All Types</SelectItem>
                {Object.entries(PO_TYPE_LABELS).map(([k, label]) => (
                  <SelectItem key={k} value={k}>{label}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Feb 2026, user's explicit ask - filter the list by Order Status
                (Created/Cancelled) so a cancelled PO (and who/when it was
                cancelled) can be found quickly instead of scrolling. */}
            <Select value={filters.order_status || "__all__"} onValueChange={(v) => setFilter("order_status", v === "__all__" ? "" : v)}>
              <SelectTrigger className="h-8 text-xs rounded-sm" data-testid="created-pos-filter-order-status"><SelectValue placeholder="Order Status" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">All Statuses</SelectItem>
                <SelectItem value="created">Created</SelectItem>
                <SelectItem value="cancelled">Cancelled</SelectItem>
              </SelectContent>
            </Select>

            <Popover open={supplierPickerOpen} onOpenChange={setSupplierPickerOpen}>
              <PopoverTrigger asChild>
                <button
                  type="button"
                  className="h-8 px-2 text-xs rounded-sm border border-[#D0D5DD] bg-white flex items-center justify-between gap-1 text-left text-[#1D2939] hover:border-[#98A2B3]"
                  data-testid="created-pos-filter-supplier"
                >
                  <span className="truncate">{supplierLabel}</span>
                  <CaretDown size={12} className="shrink-0 text-[#667085]" />
                </button>
              </PopoverTrigger>
              <PopoverContent align="start" className="w-72 p-0" data-testid="created-pos-filter-supplier-popover">
                <Command shouldFilter={true}>
                  <CommandInput
                    placeholder="Search suppliers by code or name..."
                    className="text-[13px]"
                    data-testid="created-pos-filter-supplier-search"
                    autoFocus
                  />
                  <CommandList>
                    <CommandEmpty className="text-[13px] text-[#98A2B3] py-4 text-center">No supplier found.</CommandEmpty>
                    <CommandGroup>
                      <CommandItem
                        value="__all__ all suppliers"
                        onSelect={() => { setFilter("supplier_code", ""); setSupplierPickerOpen(false); }}
                        className="text-[13px] cursor-pointer"
                        data-testid="created-pos-filter-supplier-option-all"
                      >
                        All Suppliers
                      </CommandItem>
                      {filterOptions.suppliers.map((s) => (
                        <CommandItem
                          key={s.code}
                          value={`${s.code} ${s.name}`}
                          onSelect={() => { setFilter("supplier_code", s.code); setSupplierPickerOpen(false); }}
                          className="text-[13px] cursor-pointer"
                          data-testid={`created-pos-filter-supplier-option-${s.code}`}
                        >
                          {s.code} - {s.name}
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>

            <Select value={filters.site_id || "__all__"} onValueChange={(v) => setFilter("site_id", v === "__all__" ? "" : v)}>
              <SelectTrigger className="h-8 text-xs rounded-sm" data-testid="created-pos-filter-site"><SelectValue placeholder="Plant" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">All Plants</SelectItem>
                {sites.map((s) => (
                  <SelectItem key={s.id || s} value={s.id || s}>{s.name ? `${s.id} - ${s.name}` : s}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Input
              placeholder="SAP PO #"
              className="h-8 text-xs rounded-sm font-data"
              value={poNumberFilterInput}
              onChange={(e) => setPoNumberFilterInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") setFilter("po_number", poNumberFilterInput.trim()); }}
              onBlur={() => setFilter("po_number", poNumberFilterInput.trim())}
              data-testid="created-pos-filter-po-number"
            />

            <Input
              placeholder="Item / Product ID"
              className="h-8 text-xs rounded-sm"
              value={productFilterInput}
              onChange={(e) => setProductFilterInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") setFilter("product_id", productFilterInput.trim()); }}
              onBlur={() => setFilter("product_id", productFilterInput.trim())}
              data-testid="created-pos-filter-product"
            />

            <Select value={filters.created_by || "__all__"} onValueChange={(v) => setFilter("created_by", v === "__all__" ? "" : v)}>
              <SelectTrigger className="h-8 text-xs rounded-sm" data-testid="created-pos-filter-created-by"><SelectValue placeholder="Created By" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">Anyone</SelectItem>
                {filterOptions.created_by.map((c) => (
                  <SelectItem key={c} value={c}>{c}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Input
              type="date"
              className="h-8 text-xs rounded-sm font-data"
              value={filters.po_date_from}
              onChange={(e) => setFilter("po_date_from", e.target.value)}
              data-testid="created-pos-filter-date-from"
            />
            <Input
              type="date"
              className="h-8 text-xs rounded-sm font-data"
              value={filters.po_date_to}
              onChange={(e) => setFilter("po_date_to", e.target.value)}
              data-testid="created-pos-filter-date-to"
            />
          </div>
        </div>

        <div className="bg-white border border-[#D0D5DD] rounded-sm px-3 py-2 flex flex-wrap items-center gap-x-5 gap-y-2" data-testid="created-pos-kpi-strip">
          {kpisLoading && !kpis ? (
            <span className="text-xs text-[#667085] flex items-center gap-1.5"><CircleNotch size={13} className="animate-spin" /> Loading summary...</span>
          ) : kpis ? (
            <>
              <div className="flex items-center gap-2 sm:pr-5 sm:border-r sm:border-[#EAECF0]" data-testid="created-pos-kpi-total">
                <span className="text-[10px] font-bold text-[#667085] uppercase font-heading">Total POs</span>
                <span className="text-base font-bold text-[#101828] font-heading">{kpis.total_count}</span>
              </div>

              <div className="flex items-center gap-1.5 sm:pr-5 sm:border-r sm:border-[#EAECF0] flex-wrap" data-testid="created-pos-kpi-by-site">
                <span className="text-[10px] font-bold text-[#667085] uppercase font-heading mr-0.5">By Site</span>
                {kpis.by_site.length === 0 ? (
                  <span className="text-[11px] text-[#98A2B3]">&mdash;</span>
                ) : kpis.by_site.map((s) => (
                  <span key={s.site} className="px-1.5 py-0.5 rounded-sm bg-[#F2F4F7] text-[#344054] text-[11px] font-semibold font-data" data-testid={`created-pos-kpi-site-chip-${s.site}`}>
                    {s.site}: {s.count}
                  </span>
                ))}
              </div>

              <Popover>
                <PopoverTrigger asChild>
                  <button type="button" className="flex items-center gap-1.5 sm:pr-5 sm:border-r sm:border-[#EAECF0] text-left" data-testid="created-pos-kpi-by-user-trigger">
                    <span className="text-[10px] font-bold text-[#667085] uppercase font-heading">By Creator</span>
                    <span className="text-[11px] font-semibold text-[#004B87] flex items-center gap-0.5">View Breakdown <CaretDown size={10} /></span>
                  </button>
                </PopoverTrigger>
                <PopoverContent align="start" className="w-64 p-2 max-h-72 overflow-y-auto" data-testid="created-pos-kpi-by-user-popover">
                  <div className="text-[11px] font-bold text-[#344054] uppercase font-heading mb-1.5">POs by Creator</div>
                  {kpis.by_user.length === 0 ? (
                    <div className="text-xs text-[#98A2B3] py-2 text-center">No data</div>
                  ) : kpis.by_user.map((u) => (
                    <div key={u.name} className="flex items-center justify-between py-1 text-xs border-b border-[#F2F4F7] last:border-0">
                      <span className="text-[#344054] truncate pr-2">{u.name}</span>
                      <span className="font-semibold text-[#101828] font-data">{u.count}</span>
                    </div>
                  ))}
                </PopoverContent>
              </Popover>

              <Popover>
                <PopoverTrigger asChild>
                  <button type="button" className="flex items-center gap-1.5 text-left" data-testid="created-pos-kpi-value-by-site-trigger">
                    <span className="text-[10px] font-bold text-[#667085] uppercase font-heading">PO Value by Site</span>
                    <span className="text-[11px] font-semibold text-[#004B87] flex items-center gap-0.5">View Breakdown <CaretDown size={10} /></span>
                  </button>
                </PopoverTrigger>
                <PopoverContent align="start" className="w-64 p-2 max-h-72 overflow-y-auto" data-testid="created-pos-kpi-value-by-site-popover">
                  <div className="text-[11px] font-bold text-[#344054] uppercase font-heading mb-1.5">PO Value by Site</div>
                  {kpis.value_by_site.length === 0 ? (
                    <div className="text-xs text-[#98A2B3] py-2 text-center">No data</div>
                  ) : kpis.value_by_site.map((v) => (
                    <div key={`${v.site}-${v.currency}`} className="flex items-center justify-between py-1 text-xs border-b border-[#F2F4F7] last:border-0">
                      <span className="text-[#344054]">{v.site} <span className="text-[#98A2B3] font-data">({v.currency})</span></span>
                      <span className="font-semibold text-[#101828] font-data">{formatPoValue(v.value, v.currency)}</span>
                    </div>
                  ))}
                </PopoverContent>
              </Popover>
            </>
          ) : (
            <span className="text-xs text-[#98A2B3]">Summary unavailable</span>
          )}
        </div>

        <div className="bg-white border border-[#D0D5DD] rounded-sm overflow-x-auto" data-testid="created-pos-table-card">
          {loading ? (
            <div className="p-8 flex items-center justify-center text-[#667085] text-sm" data-testid="created-pos-loading">
              <CircleNotch size={16} className="animate-spin mr-2" /> Loading...
            </div>
          ) : pos.length === 0 ? (
            <div className="p-8 text-center text-sm text-[#667085]" data-testid="created-pos-empty">
              {activeFilterCount > 0 ? "No Purchase Orders match these filters." : "No Purchase Orders have been created yet."}
            </div>
          ) : (
            <table className="w-full text-[11px] border-collapse min-w-[1050px]" data-testid="created-pos-table">
              <thead>
                <tr>
                  {COLUMNS.map((col) => (
                    <th
                      key={col.label || "actions"}
                      onClick={() => col.key && toggleSort(col.key)}
                      className={`bg-[#EAECF0] border border-[#D0D5DD] p-1.5 text-left text-xs font-bold text-[#344054] font-heading uppercase whitespace-nowrap ${col.key ? "cursor-pointer select-none hover:bg-[#E4E7EC]" : ""}`}
                      data-testid={col.key ? `created-pos-sort-${col.key}` : undefined}
                    >
                      <span className="inline-flex items-center gap-1">
                        {col.label}
                        {col.key && (
                          sortConfig.key === col.key ? (
                            sortConfig.direction === "asc"
                              ? <CaretUp size={11} weight="bold" className="text-[#004B87]" />
                              : <CaretDown size={11} weight="bold" className="text-[#004B87]" />
                          ) : (
                            <CaretUpDown size={11} className="text-[#98A2B3]" />
                          )
                        )}
                      </span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {sortedPos.map((po, idx) => (
                  <tr key={po._id} className={idx % 2 === 1 ? "bg-[#F9FAFB]" : ""} data-testid={`created-pos-row-${po.po_number}`}>
                    <td className="border border-[#D0D5DD] px-2 py-1.5 font-data font-semibold text-[#1D2939]">{po.po_number}</td>
                    <td className="border border-[#D0D5DD] px-2 py-1.5 font-data" data-testid={`created-pos-printed-number-${po.po_number}`}>{po.sap_po_number || "—"}</td>
                    <td className="border border-[#D0D5DD] px-2 py-1.5"><PoTypeBadge poType={po.po_type} /></td>
                    <td className="border border-[#D0D5DD] px-2 py-1.5"><OrderStatusCell po={po} /></td>
                    <td className="border border-[#D0D5DD] px-2 py-1.5">{po.supplier_code}</td>
                    <td className="border border-[#D0D5DD] px-2 py-1.5">{po.purchase_unit_site}</td>
                    <td className="border border-[#D0D5DD] px-2 py-1.5">{po.bill_to_company}</td>
                    <td className="border border-[#D0D5DD] px-2 py-1.5">{po.po_date}</td>
                    <td className="border border-[#D0D5DD] px-2 py-1.5">{po.pr_number || "—"}</td>
                    <td className="border border-[#D0D5DD] px-2 py-1.5">{(po.items || []).length}</td>
                    <td className="border border-[#D0D5DD] px-2 py-1.5">{po.created_by || "—"}</td>
                    <td className="border border-[#D0D5DD] px-2 py-1.5 whitespace-nowrap">{fmtDate(po.created_at)}</td>
                    <td className="border border-[#D0D5DD] px-2 py-1.5 text-center">
                      <Button type="button" variant="ghost" size="icon" className="h-7 w-7" onClick={() => setDetail(po)} data-testid={`created-pos-view-button-${po.po_number}`}>
                        <Eye size={14} className="text-[#004B87]" />
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7"
                        disabled={loadingPo === `${po.po_number}:ai`}
                        onClick={() => handlePdfButtonClick(po)}
                        data-testid={`created-pos-print-ai-pdf-button-${po.po_number}`}
                        title={po.in_preparation ? "This PO is still 'In Preparation' in SAP - click to see SAP's own reason" : "Print AI Order - our own PDF with the correct printed PO number"}
                      >
                        {loadingPo === `${po.po_number}:ai` ? <CircleNotch size={14} className="animate-spin text-[#667085]" /> : <Sparkle size={14} className={po.in_preparation ? "text-[#D92D20]" : "text-[#5925DC]"} />}
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </main>

      <Dialog open={!!detail} onOpenChange={(open) => !open && setDetail(null)}>
        <DialogContent className="max-w-2xl" data-testid="created-pos-detail-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between gap-3 pr-6">
              <span>SAP Purchase Order {detail?.po_number}</span>
              {detail && (
                <div className="flex items-center gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className={`h-7 text-xs rounded-sm ${detail.in_preparation ? "border-[#FECDCA] text-[#D92D20]" : "border-[#D9D6FE] text-[#5925DC]"}`}
                    disabled={loadingPo === `${detail.po_number}:ai`}
                    onClick={() => handlePdfButtonClick(detail)}
                    data-testid="created-pos-detail-print-ai-pdf-button"
                  >
                    {loadingPo === `${detail.po_number}:ai` ? <CircleNotch size={13} className="animate-spin mr-1.5" /> : <Sparkle size={13} className="mr-1.5" />}
                    Print AI Order
                  </Button>
                </div>
              )}
            </DialogTitle>
          </DialogHeader>
          {detail && (
            <div className="text-sm space-y-3 text-[#344054]">
              <div className="grid grid-cols-2 gap-2">
                <p><b>Supplier:</b> {detail.supplier_code}</p>
                <p><b>Printed PO #:</b> <span data-testid="created-pos-detail-printed-number">{detail.sap_po_number || "—"}</span></p>
                <p><b>Type:</b> <PoTypeBadge poType={detail.po_type} /></p>
                <p><b>Order Status:</b> <OrderStatusCell po={detail} /></p>
                <p><b>Purchase Unit:</b> {detail.purchase_unit_site}</p>
                <p><b>Company:</b> {detail.company_code}</p>
                <p><b>Bill-To:</b> {detail.bill_to_company}</p>
                <p><b>PO Date:</b> {detail.po_date}</p>
                <p><b>PR Number:</b> {detail.pr_number || "—"}</p>
                <p><b>Currency:</b> {detail.currency}</p>
                <p><b>Created By:</b> {detail.created_by || "—"}</p>
              </div>
              {detail.po_type === "interplant" && (
                <div
                  className="flex items-center justify-between gap-3 bg-[#FDF2FA] border border-[#F4ABBA] rounded-sm px-3 py-2"
                  data-testid="created-pos-detail-interplant-attachment"
                >
                  <div className="flex items-center gap-2 min-w-0">
                    <Paperclip size={16} weight="bold" className="text-[#9E165F] shrink-0" />
                    <div className="min-w-0">
                      <p className="text-xs font-semibold text-[#9E165F]">Mandatory Approval Email</p>
                      <p className="text-xs text-[#6E1C47] truncate" title={detail.attachment_filename || ""}>{detail.attachment_filename || "Attachment on file"}</p>
                    </div>
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="h-7 text-xs rounded-sm border-[#F4ABBA] text-[#9E165F] hover:bg-[#FCE7F6] shrink-0"
                    disabled={loadingPo === `${detail.po_number}:attachment`}
                    onClick={() => viewInterplantAttachment(detail.po_number)}
                    data-testid="created-pos-detail-view-attachment-button"
                  >
                    {loadingPo === `${detail.po_number}:attachment` ? <CircleNotch size={13} className="animate-spin mr-1.5" /> : <Eye size={13} className="mr-1.5" />}
                    View
                  </Button>
                </div>
              )}
              <table className="w-full text-[11px] border-collapse" data-testid="created-pos-detail-items-table">
                <thead>
                  <tr>
                    {[
                      ...(detail.po_type !== "service" && detail.po_type !== "jobwork" && detail.po_type !== "capital" ? ["Item #"] : []),
                      "Description", detail.po_type === "capital" ? "Fixed Asset" : (detail.po_type === "service" || detail.po_type === "jobwork") ? "GL Account" : "Product", "Qty", "UoM", "Unit Price", "Delivery Date",
                    ].map((h) => (
                      <th key={h} className="bg-[#EAECF0] border border-[#D0D5DD] p-1.5 text-left font-bold text-[#344054] font-heading uppercase">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {(detail.items || []).map((it, i) => (
                    <tr key={i}>
                      {/* Oct 14 2026, user's explicit ask ("line numbers are
                         flipped compared to SAP") - shows SAP's own
                         confirmed item_number (live-resequenced by the
                         refresh-prices fetch above), not the as-submitted
                         row position, so this always matches SAP's own
                         Items grid order/numbering. */}
                      {detail.po_type !== "service" && detail.po_type !== "jobwork" && detail.po_type !== "capital" && (
                        <td className="border border-[#D0D5DD] px-2 py-1.5 font-data" data-testid={`created-pos-item-number-${i}`}>{it.item_number || "—"}</td>
                      )}
                      <td className="border border-[#D0D5DD] px-2 py-1.5">{it.description || "—"}</td>
                      <td className="border border-[#D0D5DD] px-2 py-1.5 font-data">
                        {detail.po_type === "capital" ? it.fixed_asset_id : (detail.po_type === "service" || detail.po_type === "jobwork") ? it.gl_account_code : it.product_id}
                      </td>
                      <td className="border border-[#D0D5DD] px-2 py-1.5">{it.quantity}</td>
                      <td className="border border-[#D0D5DD] px-2 py-1.5">{it.unit_of_measure}</td>
                      <td className="border border-[#D0D5DD] px-2 py-1.5">
                        {it.unit_price}
                        {it.live_unit_price != null && Math.abs(it.live_unit_price - it.unit_price) > 0.01 && (
                          <div
                            className="flex items-center gap-1 text-[10px] text-[#B42318] font-bold mt-0.5 whitespace-nowrap"
                            data-testid={`created-pos-price-mismatch-${i}`}
                            title={`Approved PR rate: ${detail.currency || ""} ${it.unit_price} - SAP currently shows ${detail.currency || ""} ${it.live_unit_price}. This may indicate an unauthorized price change made directly in SAP after approval.`}
                          >
                            <WarningCircle size={11} weight="fill" /> Price Mismatch (SAP: {it.live_unit_price})
                          </div>
                        )}
                      </td>
                      <td className="border border-[#D0D5DD] px-2 py-1.5">{it.delivery_date}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!prepCheckDialog} onOpenChange={(open) => !open && setPrepCheckDialog(null)}>
        <DialogContent className="max-w-xl" data-testid="created-pos-prep-check-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-[#B42318]">
              <WarningCircle size={18} weight="fill" />
              PO {prepCheckDialog?.po_number} is still "In Preparation" in SAP
            </DialogTitle>
          </DialogHeader>
          <div className="text-sm text-[#344054] space-y-3">
            <p>A PDF can't be generated yet - SAP hasn't released this Purchase Order. Here's exactly what SAP itself is flagging right now:</p>
            {prepCheckDialog?.messages?.length > 0 ? (
              <div className="space-y-1.5" data-testid="created-pos-prep-check-messages">
                {prepCheckDialog.messages.map((msg, i) => (
                  <div
                    key={i}
                    className="flex items-start gap-2 bg-[#FEF3F2] border border-[#FECDCA] text-[#B42318] rounded-sm px-2.5 py-1.5 text-xs"
                    data-testid={`created-pos-prep-check-message-${i}`}
                  >
                    <WarningCircle size={13} weight="fill" className="mt-0.5 shrink-0" />
                    <span>{msg}</span>
                  </div>
                ))}
              </div>
            ) : (
              <div
                className="flex items-start gap-2 bg-[#FEF3F2] border border-[#FECDCA] text-[#B42318] rounded-sm px-2.5 py-1.5 text-xs"
                data-testid="created-pos-prep-check-no-messages"
              >
                <WarningCircle size={13} weight="fill" className="mt-0.5 shrink-0" />
                <span>SAP hasn't flagged a specific data issue - this PO may simply be awaiting a manual Release click in SAP.</span>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
